import React from "react";
import { Helmet } from "react-helmet-async";
import {
  DefaultCoverImage,
  DermaOfficeImage1,
  DermaOfficeImage2,
  DermaOfficeImage3,
} from "../assets/data/Imagedata";
import {
  FaUserMd,
  FaHeart,
  FaAward,
  FaShieldAlt,
  FaUsers,
  FaCheckSquare,
  FaQuoteLeft,
} from "react-icons/fa";
import { MdArrowOutward } from "react-icons/md";
import seoData from "../assets/data/seo.json";

const About = () => {
  const values = [
    {
      icon: FaUserMd,
      title: "Doctor-Driven Expertise",
      description:
        "Medical expertise guiding every procedure with strict ethical standards.",
    },
    {
      icon: FaHeart,
      title: "Emotionally Supportive Care",
      description:
        "Understanding your emotional journey and providing compassionate support.",
    },
    {
      icon: FaAward,
      title: "Long-Term Realistic Results",
      description:
        "Sustainable transformations that last through proven methods.",
    },
    {
      icon: FaShieldAlt,
      title: "Safe & Proven Methods",
      description:
        "All treatments follow strict medical protocols with proven safety standards.",
    },
  ];

  const team = [
    {
      name: "Dr. Beesetty Pavani Satya Sai Malathi",
      role: "Founder & Chief Cosmetologist",
      description:
        "Extensive expertise in Dental Cosmetology and Wellness, maintaining the highest standards of medical confidentiality and ethics.",
      image: DefaultCoverImage,
    },
    {
      name: "Mrs. Afshagul",
      role: "Co-Founder",
      description:
        "Dedicated to creating accessible and emotionally fulfilling wellness experiences for everyone.",
      image: DefaultCoverImage,
    },
  ];

  return (
    <main className="lg:mx-4">
      <Helmet>
        <title>{seoData.aboutus.title}</title>
        <meta name="description" content={seoData.aboutus.description} />
        <meta name="keywords" content={seoData.aboutus.keywords} />
        <meta property="og:title" content={seoData.aboutus.title} />
        <meta property="og:description" content={seoData.aboutus.description} />
        <meta property="og:image" content={seoData.aboutus.ogImage} />
        <link rel="canonical" href={seoData.aboutus.canonical} />
      </Helmet>

      {/* Hero Header */}
      <section className="relative bg-primary rounded-xl overflow-hidden mb-16">
        {/* Background Pattern Overlay */}
        <div className="absolute inset-0 bg-bg-pattern bg-cover bg-center opacity-30"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-20 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 text-white">
            About DermaSlim Wellness
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto">
            Where clinical science meets emotional healing
          </p>
        </div>
      </section>

      {/* About Section */}
      <section className="py-16 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-center">
            <div className="lg:w-1/2 w-full">
              {/* Photo Collage Style - Responsive */}
              <div className="relative h-[300px] sm:h-[350px] md:h-[400px] lg:h-[500px] w-full">
                {/* Main central image - slightly rotated */}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-4/5 h-3/5 sm:h-2/3 md:h-3/4 rounded-xl lg:rounded-2xl overflow-hidden shadow-lg lg:shadow-2xl z-20 rotate-2 lg:rotate-3 hover:rotate-0 transition-transform duration-300">
                  <img
                    src={DermaOfficeImage1}
                    alt="DermaSlim Wellness Clinic - Main Facility"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                </div>

                {/* Top-left image - rotated opposite direction */}
                <div className="absolute top-2 left-2 sm:top-4 sm:left-4 w-1/3 h-1/3 sm:w-2/5 sm:h-2/5 rounded-lg lg:rounded-xl overflow-hidden shadow-md lg:shadow-2xl z-30 -rotate-6 hover:rotate-0 transition-transform duration-300 border-2 sm:border-4 border-white">
                  <img
                    src={DermaOfficeImage2}
                    alt="DermaSlim Wellness Clinic - Treatment Room"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent"></div>
                </div>

                {/* Bottom-right image - rotated opposite direction */}
                <div className="absolute bottom-2 right-2 sm:bottom-4 sm:right-4 w-1/3 h-1/3 sm:w-2/5 sm:h-2/5 rounded-lg lg:rounded-xl overflow-hidden shadow-md lg:shadow-2xl z-30 -rotate-3 hover:rotate-0 transition-transform duration-300 border-2 sm:border-4 border-white">
                  <img
                    src={DermaOfficeImage3}
                    alt="DermaSlim Wellness Clinic - Consultation Area"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tl from-primary/10 to-transparent"></div>
                </div>

                {/* Decorative frame lines - only on larger screens */}
                <div className="hidden md:block absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-full border-2 border-secondary/30 rounded-3xl pointer-events-none z-0"></div>
                <div className="hidden md:block absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[85%] h-[75%] border border-primary/20 rounded-2xl pointer-events-none z-0"></div>
              </div>
            </div>

            <div className="lg:w-1/2 w-full mt-8 lg:mt-0">
              <h3 className="text-lg font-semibold text-primary mb-2">
                About Us
              </h3>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Welcome to DermaSlim Wellness
              </h2>

              <div className="text-gray-600 leading-relaxed mb-6">
                <p>
                  Founded with a vision to make holistic beauty accessible,
                  safe, and emotionally fulfilling for everyone, DermaSlim
                  Wellness was created to redefine wellness through care,
                  science, and compassion.
                </p>
              </div>

              <div className="mb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Our 4-step ritual
                </h3>
                <div className="space-y-3">
                  {["Analyze", "Prevent", "Treat", "Maintain"].map(
                    (step, index) => (
                      <div key={step} className="flex items-center gap-3">
                        <div className="w-6 h-6 bg-secondary rounded flex items-center justify-center">
                          <FaCheckSquare className="text-primary text-sm" />
                        </div>
                        <span className="text-gray-700 font-medium">
                          {step}
                        </span>
                      </div>
                    ),
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { value: "7+", label: "Years Experience" },
                  { value: "100%", label: "Patient Satisfaction" },
                  { value: "20+", label: "Team Members" },
                  { value: "15K+", label: "Successful Treatments" },
                ].map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-xl sm:text-2xl font-bold text-primary mb-1">
                      {stat.value}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-600">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Philosophy */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h3 className="text-lg font-semibold text-gray-600 mb-2">
              Our Philosophy
            </h3>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Beauty Isn't Just Skin Deep
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto text-lg">
              It's an experience that nurtures your inner confidence and overall
              wellbeing.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🔬</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Science
              </h3>
              <p className="text-gray-600">
                Evidence-based treatments with clinical precision and proven
                results.
              </p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">💖</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Compassion
              </h3>
              <p className="text-gray-600">
                Understanding your unique journey and providing personalized
                care.
              </p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🎯</span>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Personalization
              </h3>
              <p className="text-gray-600">
                Tailored solutions designed specifically for your goals and
                needs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Mission */}
            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center mb-6">
                <span className="text-2xl text-white">✨</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Our Mission
              </h3>
              <p className="text-gray-600 mb-6">
                To offer safe, personalized, and sustainable beauty & wellness
                solutions that help individuals:
              </p>
              <ul className="space-y-3">
                {["Look radiant", "Feel confident", "Live balanced"].map(
                  (item, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <FaCheckSquare className="text-green-500" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ),
                )}
              </ul>
              <p className="text-gray-600 mt-6 italic">
                All without crash diets or unrealistic expectations.
              </p>
            </div>

            {/* Vision */}
            <div className="bg-gray-50 rounded-2xl p-8">
              <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center mb-6">
                <span className="text-2xl text-white">🌍</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Our Vision
              </h3>
              <p className="text-gray-600 text-lg leading-relaxed">
                To redefine wellness in India with scientifically backed
                treatments that nurture the skin, body, mind, and self-esteem
                together.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h3 className="text-lg font-semibold text-gray-600 mb-2">
              What We Stand For
            </h3>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Core Values
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 shadow-sm border hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mb-4">
                  <value.icon className="text-xl text-gray-700" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {value.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Team */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h3 className="text-lg font-semibold text-gray-600 mb-2">
              Our Team
            </h3>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Meet Our Founders
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Dedicated professionals committed to your wellness journey
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-sm border overflow-hidden hover:shadow-md transition-shadow"
              >
                <div className="h-64 overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {member.name}
                  </h3>
                  <p className="text-blue-600 font-medium mb-3">
                    {member.role}
                  </p>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {member.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Promise */}
      <section className="py-16 bg-gray-900 text-white rounded-xl overflow-hidden">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <FaQuoteLeft className="text-4xl text-blue-400 mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Our Promise to You
          </h2>
          <blockquote className="text-xl md:text-2xl leading-relaxed mb-8 max-w-3xl mx-auto italic">
            "Your transformation is not just physical. It is holistic,
            emotional, and deeply personal. We help you unlock a version of
            yourself that feels confident, calm, and complete — all through
            gentle, enjoyable, and empowering wellness practices."
          </blockquote>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contactus"
              className="inline-flex items-center gap-2 bg-white text-gray-900 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Start Your Journey
              <MdArrowOutward className="text-md" />
            </a>
            <a
              href="/services"
              className="inline-flex items-center gap-2 border border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-gray-900 transition-colors"
            >
              Explore Services
            </a>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h3 className="text-lg font-semibold text-gray-600 mb-2">
              Why Choose Us
            </h3>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              The DermaSlim Difference
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <FaUserMd className="text-blue-600 text-xl" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Doctor-Led Care
              </h3>
              <p className="text-gray-600">
                Every treatment supervised by trained medical professionals for
                your safety.
              </p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <FaHeart className="text-green-600 text-xl" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                No Harsh Restrictions
              </h3>
              <p className="text-gray-600">
                We believe in food freedom and realistic results — no crash
                diets.
              </p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <FaUsers className="text-purple-600 text-xl" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Inclusive Environment
              </h3>
              <p className="text-gray-600">
                We welcome all age groups and body types with warmth and care.
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default About;
